import React, { useEffect, useState } from 'react';
import { ScrollView } from 'react-native';
import client from '../../../graphql/client';
import { useAuthState } from '../../auth/authContext';
import View from '../../components/styled/View';
import SessionCard from '../../components/styled/SessionCard';
import styles from './styles';
import HeaderOne from '../../components/styled/HeaderOne';
import Link from '../../components/styled/Link';
import UserError, { UserErrorCodes } from '../../components/styled/UserError';
import Monitor, { FingerPrint } from '../../lib/monitoring/Monitor';
import Modal from '../../components/styled/Modal';
import {
  NUMBER_OF_ALLOWED_SESSIONS,
  ADD_SESSION_ERROR_TEXT,
} from './constants';

export default function AristDashboardScreen({ navigation, route }) {
  const { cognitoId, email } = useAuthState();
  const [currentSessions, setCurrentSessions] = useState<any[]>([]);
  const [modalVisible, setModalVisible] = useState(false);

  // Artist State
  const [artistError, setArtistError] = useState<UserErrorCodes>();
  const [sessionsError, setSessionsError] = useState<UserErrorCodes>();

  const artistData = async () => {
    const artistInfo = await client.getArtistData(cognitoId);
    return artistInfo;
  };

  const sendMonitorData = (errorMessage, contextMessage) => {
    const monitorData = {
      context: { user_id: cognitoId, user_type: 'artist', user_email: email },
      name: 'ArtistProfile',
      fingerprint: FingerPrint.artistAccount,
      message: contextMessage,
    };
    Monitor.notify(errorMessage, monitorData);
  };

  const handleErrors = async () => {
    const rawArtistData = await artistData();
    if (!rawArtistData) {
      sendMonitorData(
        'Artist profile error',
        "Error in artistDashboard page, user doesn't have a profile",
      );
      setArtistError(UserErrorCodes.ARTIST_DASHBOARD_ERROR);
    } else {
      sendMonitorData(
        'Artist profile error',
        "Error in artistDashboard page, user doesn't have any sessions",
      );
      setSessionsError(UserErrorCodes.SESSION_ERROR);
    }
  };

  const getAllSessions = async () => {
    const allSessions = await client.getListOfSessionsByArtist(cognitoId);
    // @ts-ignore
    if (allSessions.items.length == 0) {
      handleErrors();
    } else {
      const items = allSessions.items;
      const sortedSessionsByCreatedAtDate = items.sort((a, b) =>
        b.createdAt.localeCompare(a.createdAt),
      );
      setCurrentSessions(sortedSessionsByCreatedAtDate);
    }
  };

  const goToCreateSession = async () => {
    const artistInfo = await artistData();
    if (artistInfo.sessions.items.length > NUMBER_OF_ALLOWED_SESSIONS) {
      setModalVisible(true);
    } else {
      const artistGenresArray = artistInfo.preferredGenres.items.map(
        (artistGenre) => {
          return {
            id: artistGenre.genre.id,
            genre: artistGenre.genre.genre,
          };
        },
      );
      navigation.replace('CreateSession', {
        artistGenres: artistGenresArray,
        currentSessions,
      });
    }
  };

  const toggleIsActive = async (sessId, newActiveStatus) => {
    try {
      const returnedData = await client.updateSessionActive(
        sessId,
        newActiveStatus,
      );
      const newSessions = currentSessions.map((session) => {
        if (session.id === returnedData.id) {
          return returnedData;
        }
        return session;
      });
      setCurrentSessions(newSessions);
    } catch (e) {
      console.log(e);
    }
  };
  // While we could use multiple useEffects since we are not doing anything other than invoking functions we should use one
  useEffect(() => {
    // call our sessionquery
    getAllSessions();
  }, [route?.params?.isUpdated]);

  if (artistError) {
    return (
      <ScrollView contentContainerStyle={styles.errorContainer}>
        <UserError errorType={artistError} navigation={navigation} />
      </ScrollView>
    );
  }

  if (sessionsError) {
    return (
      <ScrollView contentContainerStyle={styles.errorContainer}>
        <UserError
          errorType={sessionsError}
          handleUserErrorOverride={goToCreateSession}
          navigation={navigation}
        />
      </ScrollView>
    );
  }

  if (currentSessions.length === 0) return null;

  return (
    <ScrollView>
      <Modal
        messageText={ADD_SESSION_ERROR_TEXT}
        modalVisible={modalVisible}
        setModalVisible={setModalVisible}
      />
      <View style={styles.container}>
        <View>
          <HeaderOne style={styles.title} title="Sessions" />
          <Link
            textStyle={styles.addSessionLink}
            text="+ Add Session"
            onPress={goToCreateSession}
          />
        </View>
        <View style={styles.sessionCardsContainer}>
          {currentSessions.length > 0 &&
            currentSessions.map((session) => (
              <View key={session.id} style={styles.card}>
                <SessionCard
                  isDashboard
                  isActive={session.isActive}
                  session={session}
                  toggleIsActive={() =>
                    toggleIsActive(session.id, !session.isActive)
                  }
                  navigation={navigation}
                />
              </View>
            ))}
        </View>
      </View>
    </ScrollView>
  );
}
